import type { BulkTransferReadFileInput } from "./bulkTransfer";
import type {
  GitCheckoutInput,
  GitCheckoutResult,
  GitCreateBranchInput,
  GitPreparePullRequestThreadInput,
  GitPreparePullRequestThreadResult,
  GitPullRequestRefInput,
  GitCreateWorktreeInput,
  GitCreateWorktreeResult,
  GitInitInput,
  GitListBranchesInput,
  GitListBranchesResult,
  GitPullInput,
  GitPullResult,
  GitRemoveWorktreeInput,
  GitResolvePullRequestResult,
  GitStatusInput,
  GitStatusResult,
  GitCreateBranchResult,
} from "./git";
import type { CmmdUserIdentity } from "./cmmd";
import type { DesktopRelayHostState } from "./relay";
import type {
  BrowserCloseSessionInput,
  BrowserClearBrowsingDataInput,
  BrowserClearBrowsingDataResult,
  BrowserCommand,
  BrowserCommandInput,
  BrowserCommandResult,
  BrowserDisplayAttachInput,
  BrowserDisplayAttachResult,
  BrowserDisplayIceCandidateInput,
  BrowserDisplaySession,
  BrowserGetSessionInput,
  BrowserListSessionsInput,
  BrowserOpenSessionInput,
  BrowserSessionSnapshot,
  BrowserSubscribeInput,
  BrowserStreamEvent,
} from "./browser";
import type { ComputerInputInput, ComputerInputResult } from "./computerUse";
import type {
  DevTunnelListInput,
  DevTunnelRecord,
  DevTunnelRegisterInput,
  DevTunnelUnregisterInput,
} from "./devTunnel";
import type {
  ThreadDevRuntimeAllocatePortInput,
  ThreadDevRuntimeGetInput,
  ThreadDevRuntimeRecord,
  ThreadDevRuntimeReleaseInput,
} from "./threadDevRuntime";
import type {
  ProjectSearchContentsInput,
  ProjectSearchContentsResult,
  ProjectSearchEntriesInput,
  ProjectSearchEntriesResult,
  ProjectWriteFileInput,
  ProjectWriteFileResult,
} from "./project";
import type {
  WorkspaceEnsureScratchInput,
  WorkspaceEnsureScratchResult,
  WorkspaceEnvironmentPreflightInput,
  WorkspaceEnvironmentPreflightReport,
} from "./workspaceEnvironment";
import type {
  WorkspaceListDirectoryInput,
  WorkspaceListDirectoryResult,
} from "./workspaceDirectory";
import type {
  ServerConfig,
  ServerEnvironmentDisplayNameUpdateResult,
  ServerProviderUpdatedPayload,
  ServerRemoveKeybindingResult,
  ServerUpsertKeybindingResult,
} from "./server";
import type {
  ProviderAuthSession,
  ProviderAuthDisconnectInput,
  ProviderAuthRuntimeRepairInput,
  ProviderAuthRuntimeRepairResult,
  ProviderAuthSessionInput,
  ProviderAuthStartInput,
  ProviderAuthSubmitCodeInput,
  ProviderCredentialCloudSyncInput,
  ProviderCredentialCloudSyncResult,
} from "./providerAuth";
import type {
  TerminalClearInput,
  TerminalCloseInput,
  TerminalEvent,
  TerminalOpenInput,
  TerminalResizeInput,
  TerminalRestartInput,
  TerminalSessionSnapshot,
  TerminalSignalInput,
  TerminalWriteInput,
} from "./terminal";
import type { ServerRemoveKeybindingInput, ServerUpsertKeybindingInput } from "./server";
import type {
  ClientOrchestrationCommand,
  OrchestrationGetFullThreadDiffInput,
  OrchestrationGetFullThreadDiffResult,
  OrchestrationGetTurnDiffInput,
  OrchestrationGetTurnDiffResult,
  OrchestrationSearchThreadsInput,
  OrchestrationSearchThreadsResult,
  OrchestrationShellStreamItem,
  OrchestrationSubscribeThreadInput,
  OrchestrationThreadStreamItem,
} from "./orchestration";
import type { EnvironmentId } from "./baseSchemas";
import type {
  AttachmentCreateUploadUrlInput,
  AttachmentCreateUploadUrlResult,
  AttachmentDeleteInput,
} from "./assets";
import type {
  PullRequestActionInput,
  PullRequestActivity,
  PullRequestCommentInput,
  PullRequestCommentUpdateInput,
  PullRequestDetail,
  PullRequestDiffFileContentsInput,
  PullRequestDiffFileContentsResult,
  PullRequestInvalidateInput,
  PullRequestListInput,
  PullRequestListResult,
  PullRequestListStatsInput,
  PullRequestListStatsResult,
  PullRequestReactionInput,
  PullRequestRef,
  PullRequestReviewerCandidateList,
  PullRequestReviewerRequestInput,
  PullRequestSubmitReviewInput,
  PullRequestThreadCommentsInput,
  PullRequestThreadCommentsResult,
  PullRequestThreadReplyInput,
  PullRequestThreadResolutionInput,
  PullRequestUpdateInput,
} from "./pullRequest";
import * as Schema from "effect/Schema";
import { EditorId, type OpenInEditorInput } from "./editor";
import { ClientSettings, ServerSettings, ServerSettingsPatch } from "./settings";

export interface ContextMenuItem<T extends string = string> {
  id: T;
  label: string;
  destructive?: boolean;
  disabled?: boolean;
  /**
   * Nested items, for grouping related actions under one label.
   *
   * A parent with children is STRUCTURAL: it is never itself clickable, so its
   * `id` is never returned — only a child's is. That keeps every caller's
   * `switch (clicked)` unchanged when a flat group becomes a nested one.
   *
   * The two surfaces render this differently on purpose. Electron gets a real
   * `submenu`. The web fallback has no hover-submenu machinery, so it renders
   * the parent as a non-interactive group label with its children beneath —
   * the same actions and the same ids, reachable in one click instead of two.
   */
  children?: readonly ContextMenuItem<T>[];
}

export type DesktopUpdateStatus =
  | "disabled"
  | "idle"
  | "checking"
  | "up-to-date"
  | "available"
  | "downloading"
  | "downloaded"
  | "error";

export type DesktopRuntimeArch = "arm64" | "x64" | "other";
export type DesktopTheme = "light" | "dark" | "system";
export type DesktopUpdateChannel = "latest" | "nightly";

/**
 * One release's notes, normalized for the update popover: the newest items of
 * that release, capped, with the real total kept so the popover can say how
 * many changes it is not showing.
 */
export interface DesktopUpdateReleaseNote {
  readonly version: string;
  readonly items: ReadonlyArray<string>;
  readonly totalItems: number;
}

export interface DesktopRuntimeInfo {
  hostArch: DesktopRuntimeArch;
  appArch: DesktopRuntimeArch;
  runningUnderArm64Translation: boolean;
}

export interface DesktopUpdateState {
  enabled: boolean;
  status: DesktopUpdateStatus;
  currentVersion: string;
  /** The channel this install follows; nightly installs opt in by version. */
  channel: DesktopUpdateChannel;
  hostArch: DesktopRuntimeArch;
  appArch: DesktopRuntimeArch;
  runningUnderArm64Translation: boolean;
  availableVersion: string | null;
  downloadedVersion: string | null;
  /** Normalized notes for the pending update; empty until a check sees one. */
  releaseNotes: ReadonlyArray<DesktopUpdateReleaseNote>;
  /** Releases beyond the capped `releaseNotes` list. */
  omittedReleaseCount: number;
  downloadPercent: number | null;
  checkedAt: string | null;
  message: string | null;
  errorContext: "check" | "download" | "install" | null;
  canRetry: boolean;
}

export interface DesktopUpdateActionResult {
  accepted: boolean;
  completed: boolean;
  state: DesktopUpdateState;
}

export interface DesktopUpdateCheckResult {
  checked: boolean;
  state: DesktopUpdateState;
}

export type DesktopBackendMode = "cloud" | "local";

export interface DesktopCloudEnvironmentRecord {
  readonly httpBaseUrl: string;
  readonly wsBaseUrl: string;
  readonly environmentId: string;
  readonly label: string;
  readonly kind?: SavedEnvironmentKind;
  readonly cmmdOrganizationId?: string;
  readonly cmmdProjectId?: string;
  readonly runtimeId?: string;
  readonly runtimeTicketExpiresAt?: string;
  /**
   * A saved pre-Environment-identity key that this record replaces. It is only
   * used while migrating `cmmd-runtime:{runtimeId}` records to CMMD's durable
   * Environment id.
   */
  readonly replacesEnvironmentId?: string;
}

export interface DesktopConnectCloudRuntimeInput {
  readonly cmmdOrganizationId?: string | number | null;
  readonly cmmdProjectId?: string | number | null;
  readonly cmmdProjectName?: string | null;
  readonly githubRepoFullName?: string | null;
  readonly workspaceRoot?: string | null;
}

export interface DesktopCloudRuntimeConnection {
  readonly environment: DesktopCloudEnvironmentRecord;
  readonly bearerToken: string;
}

export interface DesktopEnvironmentBootstrap {
  readonly primaryMode?: "local";
  label: string;
  httpBaseUrl: string | null;
  wsBaseUrl: string | null;
  bootstrapToken?: string;
  bearerToken?: string;
}

export const DesktopEnvironmentBootstrap = Schema.Struct({
  primaryMode: Schema.optionalKey(Schema.Literal("local")),
  label: Schema.String,
  httpBaseUrl: Schema.NullOr(Schema.String),
  wsBaseUrl: Schema.NullOr(Schema.String),
  bootstrapToken: Schema.optionalKey(Schema.String),
  bearerToken: Schema.optionalKey(Schema.String),
});

export interface DesktopEnvironmentBootstrapCloud {
  readonly primaryMode: "cloud";
  readonly httpBaseUrl: string;
  readonly wsBaseUrl: string;
  readonly bearerToken: string;
  readonly environmentId: string;
  readonly label: string;
  readonly desktopLocal?: DesktopEnvironmentBootstrap;
  readonly cmmdOrganizationId?: string;
  readonly cmmdProjectId?: string;
  readonly runtimeId?: string;
  readonly runtimeTicketExpiresAt?: string;
}

export const DesktopEnvironmentBootstrapCloud = Schema.Struct({
  primaryMode: Schema.Literal("cloud"),
  httpBaseUrl: Schema.String,
  wsBaseUrl: Schema.String,
  bearerToken: Schema.String,
  environmentId: Schema.String,
  label: Schema.String,
  desktopLocal: Schema.optionalKey(DesktopEnvironmentBootstrap),
  cmmdOrganizationId: Schema.optionalKey(Schema.String),
  cmmdProjectId: Schema.optionalKey(Schema.String),
  runtimeId: Schema.optionalKey(Schema.String),
  runtimeTicketExpiresAt: Schema.optionalKey(Schema.String),
});

export type DesktopEnvironmentBootstrapEnvelope =
  | DesktopEnvironmentBootstrap
  | DesktopEnvironmentBootstrapCloud;

export const DesktopEnvironmentBootstrapEnvelope = Schema.Union([
  DesktopEnvironmentBootstrapCloud,
  DesktopEnvironmentBootstrap,
]);

/**
 * What an Environment is for. CMMD keys a `project` Environment to (org, user)
 * and it hosts workspaces; an `auth` Environment is the Credential Vault, which
 * holds provider credentials and hosts nothing. Forge has to be able to tell
 * them apart, because only one of the two is somewhere a project can be added.
 */
export type SavedEnvironmentKind = "project" | "auth";

export interface PersistedSavedEnvironmentRecord {
  environmentId: EnvironmentId;
  label: string;
  wsBaseUrl: string;
  httpBaseUrl: string;
  createdAt: string;
  lastConnectedAt: string | null;
  kind?: SavedEnvironmentKind;
  cmmdOrganizationId?: string;
  cmmdProjectId?: string;
  runtimeId?: string;
  runtimeTicketExpiresAt?: string;
  /** Authenticated descriptor eligibility hint; never credentials or authority. */
  lastAuthenticatedPullRequestCapability?: boolean;
}

export function sanitizeSavedEnvironmentWsBaseUrl(wsBaseUrl: string): string {
  try {
    const url = new URL(wsBaseUrl);
    if (!url.searchParams.has("runtimeTicket") && !url.searchParams.has("wsToken")) {
      return wsBaseUrl;
    }
    url.searchParams.delete("runtimeTicket");
    url.searchParams.delete("wsToken");
    return url.toString();
  } catch {
    return wsBaseUrl;
  }
}

export function normalizePersistedSavedEnvironmentRecord(
  record: PersistedSavedEnvironmentRecord,
): PersistedSavedEnvironmentRecord {
  return {
    environmentId: record.environmentId,
    label: record.label,
    httpBaseUrl: record.httpBaseUrl,
    wsBaseUrl: sanitizeSavedEnvironmentWsBaseUrl(record.wsBaseUrl),
    createdAt: record.createdAt,
    lastConnectedAt: record.lastConnectedAt,
    ...(record.kind === undefined ? {} : { kind: record.kind }),
    ...(record.cmmdOrganizationId === undefined
      ? {}
      : { cmmdOrganizationId: record.cmmdOrganizationId }),
    ...(record.cmmdProjectId === undefined ? {} : { cmmdProjectId: record.cmmdProjectId }),
    ...(record.runtimeId === undefined ? {} : { runtimeId: record.runtimeId }),
    ...(record.runtimeTicketExpiresAt === undefined
      ? {}
      : { runtimeTicketExpiresAt: record.runtimeTicketExpiresAt }),
    ...(record.lastAuthenticatedPullRequestCapability === undefined
      ? {}
      : { lastAuthenticatedPullRequestCapability: record.lastAuthenticatedPullRequestCapability }),
  };
}

export type DesktopServerExposureMode = "local-only" | "network-accessible";

export interface DesktopServerExposureState {
  mode: DesktopServerExposureMode;
  endpointUrl: string | null;
  advertisedHost: string | null;
}

export interface DesktopNotificationParams {
  /** Short title displayed in the notification banner. */
  readonly title: string;
  /** Body text shown beneath the title. */
  readonly body: string;
  /**
   * Deduplication / replacement tag. A new notification with the same tag
   * replaces any existing native notification that shares the tag.
   */
  readonly tag: string;
}

export interface DesktopNativeBrowserBounds {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface DesktopNativeBrowserIdentity {
  readonly browserId: string;
  readonly sessionId: string;
  readonly tabId?: string | null;
}

export interface DesktopNativeBrowserAttachInput extends DesktopNativeBrowserIdentity {
  readonly bounds: DesktopNativeBrowserBounds;
  readonly url?: string | null;
}

export interface DesktopNativeBrowserBoundsInput extends DesktopNativeBrowserIdentity {
  readonly bounds: DesktopNativeBrowserBounds;
}

export interface DesktopNativeBrowserDetachInput extends DesktopNativeBrowserIdentity {}

export interface DesktopNativeBrowserCommandInput extends DesktopNativeBrowserIdentity {
  readonly command: BrowserCommand;
}

export interface DesktopNativeBrowserResult {
  readonly attached: boolean;
  readonly display: BrowserDisplaySession;
  readonly title?: string | null;
  readonly url?: string | null;
}

export interface SetBackendModeInput {
  readonly mode: DesktopBackendMode;
  readonly cloudRecord?: DesktopCloudEnvironmentRecord;
  readonly bearerToken?: string;
}

export type DesktopAuthConnectionState =
  | "unavailable"
  | "unauthenticated"
  | "authenticating"
  | "authenticated"
  | "connected"
  | "disconnected"
  | "expired"
  | "error";

export type DesktopAuthIdentity = CmmdUserIdentity;

interface DesktopCmmdSessionLifecycleBase {
  readonly revision: number;
  readonly changedAt: string;
  readonly reason: string;
}

export type DesktopCmmdSessionLifecycleState =
  | (DesktopCmmdSessionLifecycleBase & {
      readonly status: "signed-out";
    })
  | (DesktopCmmdSessionLifecycleBase & {
      readonly status: "authenticating";
    })
  | (DesktopCmmdSessionLifecycleBase & {
      readonly status: "binding-local";
      readonly principalId: string | null;
      readonly nativeExpiresAt: string | null;
    })
  | (DesktopCmmdSessionLifecycleBase & {
      readonly status: "ready";
      readonly principalId: string;
      readonly nativeExpiresAt: string | null;
      readonly localSessionExpiresAt: string | null;
    })
  | (DesktopCmmdSessionLifecycleBase & {
      readonly status: "recovering";
      readonly principalId: string | null;
      readonly nativeExpiresAt: string | null;
      readonly attempt: number;
      readonly retryAt: string | null;
      readonly errorCode: string;
      readonly error: string;
    })
  | (DesktopCmmdSessionLifecycleBase & {
      readonly status: "expired";
      readonly principalId: string | null;
      readonly expiredAt: string | null;
    })
  | (DesktopCmmdSessionLifecycleBase & {
      readonly status: "error";
      readonly principalId: string | null;
      readonly errorCode: string;
      readonly error: string;
    });

export interface DesktopAuthState {
  readonly sessionLifecycle: DesktopCmmdSessionLifecycleState;
  readonly cmmd: {
    readonly state: DesktopAuthConnectionState;
    readonly identity: DesktopAuthIdentity | null;
    readonly expiresAt: string | null;
    readonly error: string | null;
  };
  readonly forgeCloud: {
    readonly state: DesktopAuthConnectionState;
    readonly environment: DesktopCloudEnvironmentRecord | null;
    readonly expiresAt: string | null;
    readonly error: string | null;
  };
  readonly github: {
    readonly state: "unknown" | "disconnected" | "connected" | "error";
    readonly error: string | null;
  };
  readonly localExecution: {
    readonly available: boolean;
    readonly httpBaseUrl: string | null;
    readonly wsBaseUrl: string | null;
  };
  readonly relayHosting?: DesktopRelayHostState;
}

export interface DesktopCmmdPasswordLoginInput {
  readonly email: string;
  readonly password: string;
}

export interface DesktopDeviceIdentity {
  readonly hostKey?: string;
  readonly deviceKey?: string;
  readonly runtimeId?: string;
  readonly displayName: string;
  readonly hostName: string | null;
  readonly computerName: string | null;
  readonly platform: string;
}

/**
 * A validated `Open locally` request: a repo and branch handed from a finished
 * cloud task to the desktop app. Both fields have already been through
 * `parseOpenLocalLink`, so they are well-formed — which is NOT the same as
 * authorized. The renderer still resolves the repo against projects the user
 * already has locally.
 */
export interface DesktopOpenLocalRequest {
  readonly repoFullName: string;
  readonly branch: string;
}

export interface DesktopBridge {
  getDeviceIdentity: () => Promise<DesktopDeviceIdentity>;
  /**
   * Native dock/taskbar badge for thread notifications. Optional because the
   * web build has no bridge at all and desktop shells built before ticket
   * 3531 predate it; the web coordinator probes both with `?.` and falls
   * back to a canvas favicon badge. Implementing them in the desktop shell is
   * the follow-up that finishes web/desktop parity here.
   */
  setNotificationBadge?: (input: { count: number; image: string | null }) => Promise<void>;
  onNotificationBadgeClear?: (listener: () => void) => () => void;
  getClientPlatform?: () => string;
  getLocalEnvironmentBootstrap: () => DesktopEnvironmentBootstrapEnvelope | null;
  getSavedEnvironmentRegistrySync?: () => readonly PersistedSavedEnvironmentRecord[];
  setBackendMode?: (input: SetBackendModeInput) => Promise<void>;
  desktopAuth?: {
    getState: () => Promise<DesktopAuthState>;
    startCmmdLogin: () => Promise<DesktopAuthState>;
    startCmmdPasswordLogin: (input: DesktopCmmdPasswordLoginInput) => Promise<DesktopAuthState>;
    refreshCloudSession: () => Promise<DesktopAuthState>;
    connectCloudRuntime: (
      input?: DesktopConnectCloudRuntimeInput,
    ) => Promise<DesktopCloudRuntimeConnection>;
    logoutCloud: () => Promise<DesktopAuthState>;
    switchToLocal: () => Promise<DesktopAuthState>;
    getEnvironmentBootstrap: () => Promise<DesktopEnvironmentBootstrapEnvelope | null>;
    onStateChange?: (listener: (state: DesktopAuthState) => void) => () => void;
    onRelayHostingState?: (listener: (state: DesktopRelayHostState) => void) => () => void;
  };
  getClientSettings: () => Promise<ClientSettings | null>;
  setClientSettings: (settings: ClientSettings) => Promise<void>;
  getSavedEnvironmentRegistry: () => Promise<readonly PersistedSavedEnvironmentRecord[]>;
  setSavedEnvironmentRegistry: (
    records: readonly PersistedSavedEnvironmentRecord[],
  ) => Promise<void>;
  getSavedEnvironmentSecret: (environmentId: EnvironmentId) => Promise<string | null>;
  setSavedEnvironmentSecret: (environmentId: EnvironmentId, secret: string) => Promise<boolean>;
  removeSavedEnvironmentSecret: (environmentId: EnvironmentId) => Promise<void>;
  getServerExposureState: () => Promise<DesktopServerExposureState>;
  setServerExposureMode: (mode: DesktopServerExposureMode) => Promise<DesktopServerExposureState>;
  pickFolder: () => Promise<string | null>;
  confirm: (message: string) => Promise<boolean>;
  setTheme: (theme: DesktopTheme) => Promise<void>;
  showContextMenu: <T extends string>(
    items: readonly ContextMenuItem<T>[],
    position?: { x: number; y: number },
  ) => Promise<T | null>;
  openExternal: (url: string) => Promise<boolean>;
  onMenuAction: (listener: (action: string) => void) => () => void;
  /**
   * Take the pending `Open locally` deep-link request, if one is waiting.
   *
   * A PULL rather than a push because the common case is a cold start: clicking
   * the link launches Forge, and the request is handled long before a renderer
   * exists to receive a pushed event. The main process holds it until the
   * renderer asks on mount.
   *
   * Taking it CLEARS it — acting on one of these checks a branch out in a real
   * working copy, so a request that survived being taken would re-run that
   * checkout on every reload.
   */
  takePendingOpenLocalRequest?: () => Promise<DesktopOpenLocalRequest | null>;
  getUpdateState: () => Promise<DesktopUpdateState>;
  checkForUpdate: () => Promise<DesktopUpdateCheckResult>;
  downloadUpdate: () => Promise<DesktopUpdateActionResult>;
  installUpdate: () => Promise<DesktopUpdateActionResult>;
  onUpdateState: (listener: (state: DesktopUpdateState) => void) => () => void;
  getUpdateChannel: () => Promise<DesktopUpdateChannel>;
  setUpdateChannel: (channel: DesktopUpdateChannel) => Promise<void>;
  /**
   * Push a native OS notification via the Electron main process.
   * Resolves once the notification has been dispatched to the OS.
   */
  pushNotification: (params: DesktopNotificationParams) => Promise<void>;
  /**
   * Subscribe to notification click events emitted by the main process.
   * Returns an unsubscribe function.
   */
  onNotificationClicked: (listener: (tag: string) => void) => () => void;
  /**
   * Subscribe to OS sleep/wake resume events from the Electron main process.
   * Returns an unsubscribe function.
   */
  onSystemResume: (listener: () => void) => () => void;
  /**
   * Electron-only native browser view lifecycle. The renderer supplies a DOM
   * rectangle; the main process owns and positions the real WebContentsView.
   */
  nativeBrowser?: {
    attach: (input: DesktopNativeBrowserAttachInput) => Promise<DesktopNativeBrowserResult>;
    command: (input: DesktopNativeBrowserCommandInput) => Promise<DesktopNativeBrowserResult>;
    updateBounds: (input: DesktopNativeBrowserBoundsInput) => Promise<DesktopNativeBrowserResult>;
    detach: (input: DesktopNativeBrowserDetachInput) => Promise<DesktopNativeBrowserResult>;
    detachAll: () => Promise<void>;
  };
}

/**
 * APIs bound to the local app shell, not to any particular backend environment.
 *
 * These capabilities describe the desktop/browser host that the user is
 * currently running: dialogs, editor/external-link opening, context menus, and
 * app-level settings/config access. They must not be used as a proxy for
 * "whatever environment the user is targeting", because in a multi-environment
 * world the local shell and a selected backend environment are distinct
 * concepts.
 */
export interface LocalApi {
  dialogs: {
    pickFolder: () => Promise<string | null>;
    confirm: (message: string) => Promise<boolean>;
  };
  shell: {
    openInEditor: (cwd: string, editor: EditorId) => Promise<void>;
    openExternal: (url: string) => Promise<void>;
  };
  contextMenu: {
    show: <T extends string>(
      items: readonly ContextMenuItem<T>[],
      position?: { x: number; y: number },
    ) => Promise<T | null>;
  };
  persistence: {
    getClientSettings: () => Promise<ClientSettings | null>;
    setClientSettings: (settings: ClientSettings) => Promise<void>;
    getSavedEnvironmentRegistry: () => Promise<readonly PersistedSavedEnvironmentRecord[]>;
    setSavedEnvironmentRegistry: (
      records: readonly PersistedSavedEnvironmentRecord[],
    ) => Promise<void>;
    getSavedEnvironmentSecret: (environmentId: EnvironmentId) => Promise<string | null>;
    setSavedEnvironmentSecret: (environmentId: EnvironmentId, secret: string) => Promise<boolean>;
    removeSavedEnvironmentSecret: (environmentId: EnvironmentId) => Promise<void>;
  };
  server: {
    getConfig: () => Promise<ServerConfig>;
    refreshProviders: () => Promise<ServerProviderUpdatedPayload>;
    startProviderAuth: (input: ProviderAuthStartInput) => Promise<ProviderAuthSession>;
    repairProviderAuthRuntime: (
      input: ProviderAuthRuntimeRepairInput,
    ) => Promise<ProviderAuthRuntimeRepairResult>;
    disconnectProviderAuth: (
      input: ProviderAuthDisconnectInput,
    ) => Promise<ServerProviderUpdatedPayload>;
    getProviderAuthSession: (input: ProviderAuthSessionInput) => Promise<ProviderAuthSession>;
    cancelProviderAuth: (input: ProviderAuthSessionInput) => Promise<ProviderAuthSession>;
    submitProviderAuthCode: (input: ProviderAuthSubmitCodeInput) => Promise<ProviderAuthSession>;
    syncPersonalCodexToCloud: (
      input: ProviderCredentialCloudSyncInput,
    ) => Promise<ProviderCredentialCloudSyncResult>;
    syncPersonalClaudeToCloud: (
      input: ProviderCredentialCloudSyncInput,
    ) => Promise<ProviderCredentialCloudSyncResult>;
    upsertKeybinding: (input: ServerUpsertKeybindingInput) => Promise<ServerUpsertKeybindingResult>;
    removeKeybinding: (input: ServerRemoveKeybindingInput) => Promise<ServerRemoveKeybindingResult>;
    getSettings: () => Promise<ServerSettings>;
    updateSettings: (patch: ServerSettingsPatch) => Promise<ServerSettings>;
    updateEnvironmentDisplayName: (
      displayName: string | null,
    ) => Promise<ServerEnvironmentDisplayNameUpdateResult>;
  };
}

/**
 * APIs bound to a specific backend environment connection.
 *
 * These operations must always be routed with explicit environment context.
 * They represent remote stateful capabilities such as orchestration, terminal,
 * project, and git operations. In multi-environment mode, each environment gets
 * its own instance of this surface, and callers should resolve it by
 * `environmentId` rather than reaching through the local desktop bridge.
 */
export interface EnvironmentApi {
  attachments: {
    createUploadUrl: (
      input: AttachmentCreateUploadUrlInput,
    ) => Promise<AttachmentCreateUploadUrlResult>;
    delete: (input: AttachmentDeleteInput) => Promise<void>;
  };
  browser?: {
    openSession: (input: BrowserOpenSessionInput) => Promise<BrowserSessionSnapshot>;
    listSessions: (
      input: BrowserListSessionsInput,
    ) => Promise<ReadonlyArray<BrowserSessionSnapshot>>;
    closeSession: (input: BrowserCloseSessionInput) => Promise<void>;
    getSession: (input: BrowserGetSessionInput) => Promise<BrowserSessionSnapshot | null>;
    command: (input: BrowserCommandInput) => Promise<BrowserCommandResult>;
    clearBrowsingData: (
      input: BrowserClearBrowsingDataInput,
    ) => Promise<BrowserClearBrowsingDataResult>;
    attachDisplay: (input: BrowserDisplayAttachInput) => Promise<BrowserDisplayAttachResult>;
    addDisplayIceCandidate: (input: BrowserDisplayIceCandidateInput) => Promise<void>;
    onEvent: (
      input: BrowserSubscribeInput,
      callback: (event: BrowserStreamEvent) => void,
    ) => () => void;
  };
  /**
   * Human control of the runtime display, used by the Desktop surface.
   *
   * Optional for the same reason `browser` is: a server can be running without
   * any display backend at all, and the caller should discover that from the
   * absent capability rather than from a failed call.
   */
  computer?: {
    input: (input: ComputerInputInput) => Promise<ComputerInputResult>;
  };
  threadDevRuntime?: {
    get: (input: ThreadDevRuntimeGetInput) => Promise<ThreadDevRuntimeRecord | null>;
    allocatePort: (input: ThreadDevRuntimeAllocatePortInput) => Promise<ThreadDevRuntimeRecord>;
    release: (input: ThreadDevRuntimeReleaseInput) => Promise<void>;
  };
  devTunnel?: {
    register: (input: DevTunnelRegisterInput) => Promise<DevTunnelRecord>;
    unregister: (input: DevTunnelUnregisterInput) => Promise<void>;
    list: (input: DevTunnelListInput) => Promise<ReadonlyArray<DevTunnelRecord>>;
  };
  terminal: {
    open: (input: typeof TerminalOpenInput.Encoded) => Promise<TerminalSessionSnapshot>;
    write: (input: typeof TerminalWriteInput.Encoded) => Promise<void>;
    resize: (input: typeof TerminalResizeInput.Encoded) => Promise<void>;
    signal: (input: typeof TerminalSignalInput.Encoded) => Promise<void>;
    clear: (input: typeof TerminalClearInput.Encoded) => Promise<void>;
    restart: (input: typeof TerminalRestartInput.Encoded) => Promise<TerminalSessionSnapshot>;
    close: (input: typeof TerminalCloseInput.Encoded) => Promise<void>;
    onEvent: (callback: (event: TerminalEvent) => void) => () => void;
  };
  projects: {
    searchContents: (
      input: ProjectSearchContentsInput,
      options?: EnvironmentRequestOptions,
    ) => Promise<ProjectSearchContentsResult>;
    searchEntries: (input: ProjectSearchEntriesInput) => Promise<ProjectSearchEntriesResult>;
    readFile: (
      input: BulkTransferReadFileInput,
      options?: EnvironmentTransferOptions,
    ) => Promise<Uint8Array>;
    writeFile: (
      input: ProjectWriteFileInput,
      options?: EnvironmentTransferOptions,
    ) => Promise<ProjectWriteFileResult>;
  };
  workspace: {
    environmentPreflight: (
      input: WorkspaceEnvironmentPreflightInput,
    ) => Promise<WorkspaceEnvironmentPreflightReport>;
    /**
     * One workspace directory's immediate children, for the Files tree. The
     * server confines the listed path to the workspace root.
     */
    listDirectory: (input: WorkspaceListDirectoryInput) => Promise<WorkspaceListDirectoryResult>;
    /**
     * Resolve (creating it if needed) the runtime's scratch workspace — the
     * directory a thread runs in when the user chose "Don't use a workspace".
     * The runtime owns the path because only it knows whether it is a cloud
     * sprite rooted at `FORGE_CLOUD_PROJECTS_ROOT` or a desktop machine.
     */
    ensureScratch: (input: WorkspaceEnsureScratchInput) => Promise<WorkspaceEnsureScratchResult>;
  };
  /** Runtime-scoped host shell actions. Never substitute the local app shell. */
  shell: {
    openInEditor: (input: OpenInEditorInput) => Promise<void>;
  };
  git: {
    listBranches: (input: GitListBranchesInput) => Promise<GitListBranchesResult>;
    createWorktree: (input: GitCreateWorktreeInput) => Promise<GitCreateWorktreeResult>;
    removeWorktree: (input: GitRemoveWorktreeInput) => Promise<void>;
    createBranch: (input: GitCreateBranchInput) => Promise<GitCreateBranchResult>;
    checkout: (input: GitCheckoutInput) => Promise<GitCheckoutResult>;
    init: (input: GitInitInput) => Promise<void>;
    resolvePullRequest: (input: GitPullRequestRefInput) => Promise<GitResolvePullRequestResult>;
    preparePullRequestThread: (
      input: GitPreparePullRequestThreadInput,
    ) => Promise<GitPreparePullRequestThreadResult>;
    pull: (input: GitPullInput) => Promise<GitPullResult>;
    refreshStatus: (input: GitStatusInput) => Promise<GitStatusResult>;
    onStatus: (
      input: GitStatusInput,
      callback: (status: GitStatusResult) => void,
      options?: {
        onResubscribe?: () => void;
      },
    ) => () => void;
  };
  pullRequests: {
    list: (input: PullRequestListInput) => Promise<PullRequestListResult>;
    listStats: (input: PullRequestListStatsInput) => Promise<PullRequestListStatsResult>;
    detail: (input: PullRequestRef) => Promise<PullRequestDetail>;
    activity: (input: PullRequestRef) => Promise<PullRequestActivity>;
    threadComments: (
      input: PullRequestThreadCommentsInput,
    ) => Promise<PullRequestThreadCommentsResult>;
    diffFileContents: (
      input: PullRequestDiffFileContentsInput,
    ) => Promise<PullRequestDiffFileContentsResult>;
    runAction: (input: PullRequestActionInput) => Promise<void>;
    update: (input: PullRequestUpdateInput) => Promise<void>;
    comment: (input: PullRequestCommentInput) => Promise<void>;
    updateComment: (input: PullRequestCommentUpdateInput) => Promise<void>;
    submitReview: (input: PullRequestSubmitReviewInput) => Promise<void>;
    replyToThread: (input: PullRequestThreadReplyInput) => Promise<void>;
    setThreadResolution: (input: PullRequestThreadResolutionInput) => Promise<void>;
    setReaction: (input: PullRequestReactionInput) => Promise<void>;
    invalidate: (input: PullRequestInvalidateInput) => Promise<void>;
    reviewerCandidates: (input: PullRequestRef) => Promise<PullRequestReviewerCandidateList>;
    requestReviewers: (input: PullRequestReviewerRequestInput) => Promise<void>;
  };
  orchestration: {
    dispatchCommand: (command: ClientOrchestrationCommand) => Promise<{ sequence: number }>;
    searchThreads: (
      input: OrchestrationSearchThreadsInput,
    ) => Promise<OrchestrationSearchThreadsResult>;
    getTurnDiff: (
      input: OrchestrationGetTurnDiffInput,
      options?: EnvironmentTransferOptions,
    ) => Promise<OrchestrationGetTurnDiffResult>;
    getFullThreadDiff: (
      input: OrchestrationGetFullThreadDiffInput,
      options?: EnvironmentTransferOptions,
    ) => Promise<OrchestrationGetFullThreadDiffResult>;
    subscribeShell: (
      callback: (event: OrchestrationShellStreamItem) => void,
      options?: {
        onResubscribe?: () => void;
      },
    ) => () => void;
    subscribeThread: (
      input: OrchestrationSubscribeThreadInput,
      callback: (event: OrchestrationThreadStreamItem) => void,
      options?: {
        onResubscribe?: () => void;
      },
    ) => () => void;
  };
}

export interface EnvironmentTransferProgress {
  readonly completedBytes: number;
  readonly totalBytes: number;
}

/** Request lifecycle controls that do not cross the environment RPC wire. */
export interface EnvironmentRequestOptions {
  readonly signal?: AbortSignal;
}

export interface EnvironmentTransferOptions {
  readonly signal?: AbortSignal;
  readonly onProgress?: (progress: EnvironmentTransferProgress) => void;
}
